import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loadHelpMenuSections, type HelpMenuSection } from './helpMenuData';

const HELP_HUB = '/help-centre';

/** Hover-intent delays — mockup openHelp/closeHelp (L1308–1316). */
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 400;

/** Help nav item with its hover mega-menu — source: mockup L65–130. */
const HelpMegaMenu = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The route the panel was opened on. Any navigation resets it during render
  // (React's "adjusting state when a prop changes" pattern), which both closes
  // the panel on route change and stops it reappearing if the user navigates
  // back to the route it was opened on. Keying by pathname additionally makes an
  // in-flight open timer resolve to a stale route, so it cannot pop the panel
  // open on the page you just landed on.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  // Real help-centre content, fetched on first hover intent — see helpMenuData.
  const [sections, setSections] = useState<HelpMenuSection[] | null>(null);
  const [renderedPath, setRenderedPath] = useState(pathname);
  if (renderedPath !== pathname) {
    setRenderedPath(pathname);
    setOpenedOn(null);
  }
  const open = openedOn === pathname;

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = undefined;
    closeTimer.current = undefined;
  }, []);

  const scheduleOpen = useCallback(() => {
    // Kick off the content load alongside the hover-intent delay so the
    // panel usually opens already populated. A failed load leaves the grid
    // empty for this open; the next hover retries.
    void loadHelpMenuSections().then(setSections).catch(() => {});
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setOpenedOn(pathname), OPEN_DELAY_MS);
  }, [pathname]);

  const scheduleClose = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = undefined;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenedOn(null), CLOSE_DELAY_MS);
  }, []);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpenedOn(null);
  }, [clearTimers]);

  // Escape closes the panel wherever focus happens to be.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNow();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeNow]);

  useEffect(() => clearTimers, [clearTimers]);

  return (
    <div
      className="oq-help"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        className="oq-header__nav-link oq-help__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          closeNow();
          navigate(HELP_HUB);
        }}
      >
        Help
        <i className="mdi mdi-chevron-down oq-help__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="oq-help__anchor">
          <span className="oq-help__arrow" aria-hidden="true" />
          <div className="oq-help__panel">
            <div className="oq-help__ai">
              <img src="/images/oq-avatar.png" alt="" className="oq-help__ai-avatar" />
              <div className="oq-bebas oq-help__ai-eyebrow">OQ · AI ASSISTANT</div>
              <div className="oq-bebas oq-help__ai-title">
                Ask OQ
                <br />
                anything
              </div>
              <p className="oq-help__ai-copy">
                A straight answer on sessions, payments or memberships — instantly.
              </p>
              <div className="oq-help__ai-chat">
                <div className="oq-help__ai-bubble-row">
                  <span className="oq-help__ai-bubble">How do refunds work?</span>
                </div>
                <div className="oq-help__ai-dots" aria-hidden="true">
                  <span className="oq-help__ai-dot" />
                  <span className="oq-help__ai-dot" />
                  <span className="oq-help__ai-dot" />
                </div>
              </div>
              <div className="oq-help__ai-cta-wrap">
                <Link to={HELP_HUB} className="oq-help__ai-cta" onClick={closeNow}>
                  Start chatting
                  <i className="mdi mdi-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="oq-help__guides">
              <div className="oq-help__guides-head">
                <span className="oq-bebas oq-help__guides-title">BROWSE GUIDES</span>
                <Link to={HELP_HUB} className="oq-help__all" onClick={closeNow}>
                  All guides
                  <i className="mdi mdi-arrow-right" aria-hidden="true" />
                </Link>
              </div>
              <div className="oq-help__grid">
                {(sections ?? []).map((section) => (
                  <div key={section.label} className="oq-help__section">
                    <Link to={section.to} className="oq-help__section-head" onClick={closeNow}>
                      <i className={`mdi ${section.icon} oq-help__section-icon`} aria-hidden="true" />
                      <span className="oq-help__section-label">{section.label}</span>
                    </Link>
                    {section.items.map((item) => (
                      <Link key={item.label} to={item.to} className="oq-help__item" onClick={closeNow}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpMegaMenu;
